<script setup lang="ts">
import axios from 'axios'
import { Plus } from 'lucide-vue-next'

import EmptyState from '@/components/EmptyState.vue'
import TemplateModal from '@/components/TemplateModal.vue'
import CampaignItems from './CampaignPage/CampaignItems.vue'
import useContentScopeList, { useContentScopeCriteria } from '@/composables/contentScopeList'
import { contentScopeListParams } from '@/utils/contentScope'
import useCampaignListFilters, { useCampaignListQuery } from './composables/campaignListFilters'

import type { CampaignSortKey, CampaignStatistics, CampaignType } from '@/types/types'
import CampaignStatisticsCards from './components/CampaignStatisticsCards.vue'

const route = useRoute()
const { t } = useI18n()
const { permissions, stores } = useLocalStorage()
const { fetchTemplates } = useTemplate()
const { selectedStoreFilter } = useStore()
const notification = useNotification()

const isLoading = ref(true)
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const campaigns = ref([])
const statistics = ref<CampaignStatistics>()
const pagination = ref({
  currentPage: 1,
  lastPage: null,
  total: null,
  pageSize: 15
})

const campaignType = computed<CampaignType>(() => (route.query.type as CampaignType) || 'sms')
const pageTitle = computed(() => ({
  title: `pages.campaign.campaignPage.${campaignType.value}.title`
}))

const showCampaignCreateButton = computed(
  () => permissions.value['campaign.create'] && (campaigns.value.length > 0 || hasListFilters.value)
)

const isTemplateTipsOpen = ref(false)
const hasFetchedTemplates = ref(false)
const templates = ref([])

const openTemplatesTips = async () => {
  if (!hasFetchedTemplates.value) {
    await fetchOnceTemplates()
  }
  isTemplateTipsOpen.value = true
}

const fetchOnceTemplates = async () => {
  templates.value = await fetchTemplates('campaign')
  hasFetchedTemplates.value = true
}

const isFetching = ref(false)
const sort = ref<CampaignSortKey>('updated_at')

const { campaignListParams, fetchCampaignTotal } = useCampaignListQuery(campaignType, storeFilter)

const contentScope = useContentScopeList('campaign', {
  fetchTotal: fetchCampaignTotal,
  reload: () => {
    pagination.value.currentPage = 1
    return fetchData()
  },
  countsKey: () => [campaignType.value, storeFilter.value]
})

const hasListFilters = computed(() => contentScope.hasActiveFilters.value)

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const { statusCriterion, sortOptions } = useCampaignListFilters(contentScope, { withDraft: false })

const filterCriteria = computed(() => [origin.value, createdBy.value, statusCriterion.value])

const fetchData = async () => {
  try {
    isFetching.value = true

    const urlParams = {
      page: pagination.value.currentPage,
      per_page: 15,
      sort: sort.value,
      ...campaignListParams(contentScopeListParams(contentScope.filters.value))
    }

    const { data } = await axios.get('campaigns', { params: urlParams })
    const campaignsData = data.data

    pagination.value.total = data.meta.total
    pagination.value.lastPage = data.meta.last_page
    pagination.value.pageSize = data.meta.per_page

    if (campaignsData.length > 0) {
      try {
        const ids = campaignsData.map((campaign) => campaign.id)

        let params: {
          ids: number[]
          type: CampaignType
          storeIds?: number[]
        } = {
          ids,
          type: campaignType.value,
          ...(storeFilter.value.length > 0 &&
            storeFilter.value.length < stores.value.length && { storeIds: storeFilter.value })
        }

        const { data: statisticsResponse } = await axios.get('campaigns/statistics', {
          params
        })

        const statisticsMap = new Map(statisticsResponse.data.map((stat) => [stat.campaignId, stat]))

        campaignsData.forEach((campaign) => {
          campaign.statistics = statisticsMap.get(campaign.id) ?? null
        })
      } catch {
        campaignsData.forEach((campaign) => {
          campaign.statistics = null
        })
        notification.create('error', {
          text: t('pages.campaign.campaignPage.notifications.statisticsError'),
          timeout: 5000,
          persist: false
        })
      }
    }

    campaigns.value = campaignsData
  } finally {
    isLoading.value = false
    isFetching.value = false
  }
}

const changePage = async (page) => {
  pagination.value.currentPage = page
  await fetchData()
}

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await Promise.all([fetchData(), getGeneralStatistics()])
})

watch(sort, async () => {
  pagination.value.currentPage = 1
  await fetchData()
})

const getGeneralStatistics = async () => {
  const { data } = await axios.get('campaigns/statistics/general', {
    params: {
      storeIds:
        storeFilter.value.length > 0 && storeFilter.value.length < stores.value.length ? storeFilter.value : null,
      type: campaignType.value
    }
  })

  statistics.value = data.data
}

watch(
  () => route.query.type,
  async () => {
    await Promise.all([fetchData(), getGeneralStatistics()])
  }
)

onMounted(async () => {
  await getGeneralStatistics()

  if (campaigns.value.length === 0) await fetchData()
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="isLoading" />
    <div v-else>
      <div>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button v-if="showCampaignCreateButton" variant="default-primary" @click="openTemplatesTips">
              <Plus />
              {{ t(`pages.campaign.campaignPage.${campaignType}.cta.create`) }}
            </Button>
          </template>
        </TheListHeader>

        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <CampaignStatisticsCards v-if="campaigns.length > 0 && statistics" :statistics="statistics" />

          <div class="mb-6 flex items-center gap-2 text-blue-600">
            <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />
            <span class="text-sm">{{ t('global.stats.upgradeAt') }}</span>
          </div>

          <ListToolbar class="mb-4" :criteria="filterCriteria">
            <template #leading>
              <MultiSelectStores v-model="storeFilter" :has-label="false" />
            </template>
            <template #trailing>
              <ListSortMenu v-model="sort" :options="sortOptions" />
            </template>
          </ListToolbar>

          <CampaignItems
            v-if="campaigns.length"
            :campaigns="campaigns"
            :pagination="pagination"
            :is-fetching="isFetching"
            @update:change-page="changePage"
          />
          <ContentScopeEmptyState
            v-else-if="hasListFilters"
            :is-mine-view="contentScope.isMineView.value"
            :subject="contentScope.subject"
            @reset="contentScope.resetFilters()"
          />
          <EmptyState v-else module="campaign" :url-params="{ type: campaignType }" />
        </div>
      </div>
    </div>
    <TemplateModal
      :is-open="isTemplateTipsOpen"
      module="campaign"
      :templates="templates"
      :url-params="{ type: campaignType }"
      @on-close-modal="isTemplateTipsOpen = false"
    />
  </div>
</template>
